import { cn } from "@/lib/utils";
import { BasicTooltip } from "../Tooltip";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ProgressBarProps, ProgressBarSize } from "./types";
import { PHRASES, sizeClasses } from "./constants";

export const ProgressBar = ({
  isLoading,
  progress,
  failedTooltipText,
  successTooltipText,
  showProgress,
  showPhrase,
  className,
  fullWidth = false,
  size = ProgressBarSize.MEDIUM,
}: ProgressBarProps) => {
  const sizeConfig = sizeClasses[size];
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);

  useEffect(() => {
    if (isLoading && showPhrase) {
      const interval = setInterval(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % PHRASES.length);
      }, 2500);

      return () => clearInterval(interval);
    }
  }, [isLoading, showPhrase]);

  return (
    <div
      className={cn(
        "flex w-fit flex-col items-center gap-2",
        fullWidth && "w-full",
      )}
    >
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-md bg-gray-200",
            sizeConfig.container,
            className,
          )}
        >
          <BasicTooltip
            shouldShow={Boolean(
              !isLoading && failedTooltipText && progress < 100,
            )}
            content={failedTooltipText}
          >
            <div
              style={{
                width: `${100 - progress}%`,
              }}
              className={cn(
                "absolute right-0 top-0 h-full",
                isLoading ? "hidden" : "bg-red-400",
              )}
            />
          </BasicTooltip>
          <BasicTooltip
            shouldShow={Boolean(successTooltipText && progress > 0)}
            content={successTooltipText}
          >
            <div
              style={{
                width: `${progress}%`,
                transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              className={cn(
                "absolute left-0 top-0 flex h-full flex-col justify-center overflow-hidden text-center text-white",
                isLoading ? "rounded-r bg-blue-500" : "bg-green-400",
              )}
            >
              {isLoading && progress > 0 && (
                <div
                  className="absolute inset-0 -translate-x-full animate-shimmer opacity-30"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, #ffffff, transparent)",
                    width: "200%",
                  }}
                />
              )}
            </div>
          </BasicTooltip>
        </div>
        {showProgress && (
          <span
            className={cn(
              "w-8 text-right text-gray-500 transition-opacity duration-300",
              sizeConfig.text,
            )}
            style={{
              opacity: isLoading ? 1 : 0.7,
            }}
          >
            {progress}%
          </span>
        )}
      </div>
      {showPhrase && (
        <div className="flex h-4 items-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.span
              key={currentPhraseIndex}
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={cn("block text-gray-500", sizeConfig.text)}
            >
              {PHRASES[currentPhraseIndex]}
            </motion.span>
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export { ProgressBarSize } from "./types";
